import React, { useMemo } from "react";
import { useTable, useSortBy, usePagination } from "react-table";
import { useNavigate } from "react-router-dom";

const Table = ({
  columns,
  data,
  paginationDisabled = null,
  onRowClick = false,
  path = null
}) => {
  const navigate = useNavigate();

  const memoizedColumns = useMemo(() => columns, [columns]);
  const memoizedData = useMemo(() => data, [data]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    state: { pageIndex, pageSize },
    canPreviousPage,
    canNextPage,
    pageCount,
    gotoPage,
    previousPage,
    nextPage,
    setPageSize,
  } = useTable(
    {
      columns: memoizedColumns,
      data: memoizedData,
      defaultColumn: {
        minWidth: 30,
        width: 'auto',
        maxWidth: 'auto',
      },
      initialState: { pageIndex: 0, pageSize: 5 },
    },
    useSortBy,
    usePagination
  );

  // SVG components for Previous and Next buttons
  const PreviousButtonSVG = ({ disabled }) => (
    <svg
      width="32"
      height="33"
      viewBox="0 0 32 33"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect
        width="32"
        height="32"
        rx="5.33333"
        transform="matrix(-1 0 0 1 32 0.5)"
        fill="white"
      />
      <path
        d="M18 21.5L13.42 16.4992L18 11.5"
        stroke={disabled ? "#898989" : "#2D71E2"}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  const NextButtonSVG = ({ disabled }) => (
    <svg
      width="32"
      height="33"
      viewBox="0 0 32 33"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect y="0.5" width="32" height="32" rx="5.33333" fill="white" />
      <path
        d="M14 21.5L18.58 16.4992L14 11.5"
        stroke={disabled ? "#898989" : "#2D71E2"}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  // Generate pagination items (page numbers and ellipsis)
  const getPaginationItems = () => {
    const maxVisiblePages = 6;
    const items = [];
    const total = pageCount;

    if (total <= maxVisiblePages) {
      for (let i = 0; i < total; i++) items.push(i);
    } else {
      const startPage = Math.max(0, pageIndex - 2);
      const endPage = Math.min(pageCount - 1, startPage + maxVisiblePages - 1);

      if (startPage > 0) {
        items.push(0);
        if (startPage > 1) items.push("...");
      }

      for (let i = startPage; i <= endPage; i++) {
        items.push(i);
      }

      if (endPage < pageCount - 1) {
        if (endPage < pageCount - 2) items.push("...");
        items.push(pageCount - 1);
      }
    }
    return items;
  };
  const handleRowClick = (row) => {
    if (onRowClick && path) {
      navigate(`${path}/${row.id}`);
    }
  };
  return (
    <div
      className={`reservation-table  ${paginationDisabled ? "bg-white tableAvis" : "oferflowTable"
        }`}
    >
      {/* <table {...getTableProps()}  className={`${paginationDisabled ? "borderTbleAvis" : "width-100"}`}> */}
      <table {...getTableProps()} className="width-100">
        <thead>
          {headerGroups.map((headerGroup) => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column) => (
                <th
                  {...column.getHeaderProps(column.getSortByToggleProps())}
                  className={`body ${column.className || ""}`}
                  style={{ minWidth: column?.width }}
                >
                  {column.render("Header")}
                  {column.canSort && (
                    <span>
                      {column.isSorted ? (
                        column.isSortedDesc ? (
                          <img
                            src="/images/sort-desc.svg"
                            alt="Tri décroissant"
                          />
                        ) : (
                          <img src="/images/sort-asc.svg" alt="Tri croissant" />
                        )
                      ) : (
                        <img src="/images/sort.svg" alt="Trier" />
                      )}
                    </span>
                  )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()} className="body grey66">
          {page.map((row, index) => {
            prepareRow(row);
            return (
              <tr
                {...row.getRowProps()}
                style={{
                  backgroundColor: index % 2 === 0 ? "#f9f9f9" : "#fff",
                }}
                onClick={() => handleRowClick(row?.original)}
                role="button"
                tabIndex={0}
              >
                {row.cells.map((cell) => (
                <td
                  {...cell.getCellProps({
                    className: cell.column.className, 
                  })}
                >
                  {cell.render("Cell")}
                </td>
              ))}

              </tr>
            );
          })}
        </tbody>
      </table>
      {!paginationDisabled && (
        <div className="pagination-controls justify-content-between mt-28">
          <div className="results-per-page">
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                gotoPage(0); // Reset to first page when changing page size
              }}
              className="form-select-option"
            >
              {[5, 10, 20].map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
            <label htmlFor="resultsPerPage" style={{ marginRight: "10px" }}>
              éléments par page
            </label>
          </div>
          <div
            className="pagination-buttons"
            style={{ display: "flex", alignItems: "center", gap: "10px" }}
          >
            <button
              onClick={previousPage}
              disabled={!canPreviousPage}
              className="btn twyny-cta-secondary"
              style={{ border: "none", background: "none", padding: 0 }}
              aria-label="Page précédente"
              aria-disabled={!canPreviousPage}
            >
              <PreviousButtonSVG disabled={!canPreviousPage} />
            </button>
            {getPaginationItems().map((page, index) =>
              page === "..." ? (
                <span key={`ellipsis-${index}`} style={{ padding: "8px 14px" }}>
                  ...
                </span>
              ) : (
                <button
                  key={page}
                  onClick={() => gotoPage(page)}
                  style={{
                    width: pageIndex === page ? "38px" : "auto",
                    height: pageIndex === page ? "37px" : "auto",
                    borderRadius: pageIndex === page ? "8px" : "0",
                    padding: pageIndex === page ? "8px 14px" : "8px",
                    background: pageIndex === page ? "#F6F6F6" : "none",
                    border: "none",
                    cursor: "pointer",
                    textAlign: "center",
                  }}
                  aria-label={`Page ${page + 1}`}
                >
                  {page + 1}
                </button>
              )
            )}
            <button
              onClick={nextPage}
              disabled={!canNextPage}
              className="btn twyny-cta-secondary"
              style={{ border: "none", background: "none", padding: 0 }}
              aria-label="Page suivante"
              aria-disabled={!canNextPage}
            >
              <NextButtonSVG disabled={!canNextPage} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Table;
